import { type FC } from 'react';
import { useSelector } from 'react-redux';
import { Alert } from 'react-bootstrap';

import { type Conflict, type ReorderConflict, ConflictType } from '@editor2/types/conflict';
import { type RootState } from '@editor2/reducers/root';
import { sanitizeAttributeName } from '../CourseChangesDiff/helpers';
import IncomingChangesDivider from './common/IncomingChangesDivider';
import ConflictDiffView from './common/ConflictDiffView';
import DeletedModifiedView from './conflictView/DeletedModifiedView';
import ModifiedDeletedView from './conflictView/ModifiedDeletedView';
import InitialCurrentReorderView from './conflictView/InitialCurrentReorder';
import InitialIncomingReorderView from './conflictView/InitialIncomingReorder';
import { getEntityName } from './helpers';
import { conflictContainer } from './styles';
import MergedChangesDivider from './common/MergedChangesDivider';
import CurrentChangesDivider from './common/CurrentChangesDivider';
import InitialMergedReorderView from './conflictView/InitialMergedReorderView';
import ItemMoveView from './conflictView/ItemMoveView';

interface Props {
	conflict: Conflict | ReorderConflict;
	courseId: number;
}

const ConflictBlock: FC<Props> = (props) => {
	const { conflict, courseId } = props;

	const courseState = useSelector((state: RootState) => state.courses[courseId]);

	const checkDeletedModifiedConflictsPresent = () => {
		return !!courseState.conflicts.find(
			(item) => item.conflictType === ConflictType.DeletedModified
		);
	};

	const checkModifiedDeletedConflictsPresent = () => {
		return !!courseState.conflicts.find(
			(item) => item.conflictType === ConflictType.ModifiedDeleted
		);
	};

	const showAcceptButton = () => {
		return !(
			conflict.conflictType === ConflictType.ChildrenReorder &&
			(checkDeletedModifiedConflictsPresent() || checkModifiedDeletedConflictsPresent())
		);
	};

	return (
		<div className={conflictContainer}>
			<div className="conflictNameBlock">
				<div>{getEntityName(conflict.entityId, conflict.entityType, courseState)}</div>
				<div className="conflictName">
					{sanitizeAttributeName(conflict.attribute)}{' '}
					{conflict.conflictType !== ConflictType.Property && conflict.conflictType} conflict
				</div>
			</div>

			<div>
				{!showAcceptButton() && (
					<Alert variant="warning" className="conflictsAlert">
						Please fix all <strong>Deleted-Modified</strong> and <strong>Modified-Deleted</strong>{' '}
						conflicts first
					</Alert>
				)}

				{conflict.conflictType !== ConflictType.ItemMove && (
					<CurrentChangesDivider
						courseId={courseId}
						conflict={conflict}
						showButton={showAcceptButton()}
					/>
				)}

				{conflict.conflictType === ConflictType.ModifiedDeleted && (
					<ModifiedDeletedView
						conflict={conflict as Conflict}
						courseId={courseId}
						deletedEntityName={getEntityName(conflict.entityId, conflict.entityType, courseState)}
						showButton={showAcceptButton()}
					/>
				)}

				{conflict.conflictType === ConflictType.DeletedModified && (
					<DeletedModifiedView
						conflict={conflict as Conflict}
						courseId={courseId}
						deletedEntityName={getEntityName(conflict.entityId, conflict.entityType, courseState)}
						showButton={showAcceptButton()}
					/>
				)}

				{conflict.conflictType === ConflictType.ChildrenReorder && (
					<>
						<InitialCurrentReorderView conflict={conflict as ReorderConflict} courseId={courseId} />

						<IncomingChangesDivider
							courseId={courseId}
							conflict={conflict}
							showButton={showAcceptButton()}
						/>

						<InitialIncomingReorderView
							conflict={conflict as ReorderConflict}
							courseId={courseId}
						/>

						<MergedChangesDivider
							courseId={courseId}
							conflict={conflict}
							showButton={showAcceptButton()}
						/>

						<InitialMergedReorderView conflict={conflict as ReorderConflict} courseId={courseId} />
					</>
				)}

				{conflict.conflictType === ConflictType.Property && (
					<>
						<ConflictDiffView
							oldValue={(conflict as Conflict).initial}
							newValue={(conflict as Conflict).current}
						/>

						<IncomingChangesDivider
							courseId={courseId}
							conflict={conflict}
							showButton={showAcceptButton()}
						/>

						<ConflictDiffView
							oldValue={(conflict as Conflict).initial}
							newValue={(conflict as Conflict).incoming}
						/>

						{typeof conflict.initial === 'string' && (
							<>
								<MergedChangesDivider
									courseId={courseId}
									conflict={conflict}
									showButton={showAcceptButton()}
								/>

								<ConflictDiffView
									oldValue={(conflict as Conflict).initial}
									newValue={(conflict as Conflict).current + ' ' + (conflict as Conflict).incoming}
								/>
							</>
						)}
					</>
				)}

				{conflict.conflictType === ConflictType.ItemMove && (
					<ItemMoveView conflict={conflict as Conflict} courseId={courseId} />
				)}
			</div>
		</div>
	);
};

export default ConflictBlock;
